import React, { Component } from 'react';
import { Card, Form, Button, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';
import * as Cookies from 'es-cookie';

const THEME = {
  primary: "#6366f1",
  success: "#10b981",
  danger: "#ef4444",
  text: "#0f172a",
  textMuted: "#64748b",
  border: "#e2e8f0",
  bgLight: "#f8fafc",
  lockedBg: "#e2e8f0"
};

// ── QUERIES ──────────────────────────────────────────────────
const GET_ADMISSION_FORMS = gql`
  query($institute_id: Int!, $branch_id: String) {
    getAdmissionForms(institute_id: $institute_id, branch_id: $branch_id) {
      id
      form_name
      academic_year_id
    }
  }
`;

const GET_ADMISSION_FIELDS = gql`
  query($admission_form_id: Int!) {
    getAdmissionFields(admission_form_id: $admission_form_id) {
      id
      field_name
      field_type_id
      field_key
      options
      value_length
      is_required
      order_no
    }
  }
`;

const GET_ACADEMIC_YEARS = gql`
  query getAcademicYears($id: Int) {
    getAcademicYears(id: $id) {
      id
      academic_year
    }
  }
`;

// ── MUTATIONS ────────────────────────────────────────────────
const CREATE_ADMISSION_FORM = gql`
  mutation($input: CreateAdmissionFormInput!) {
    createAdmissionForm(input: $input) {
      id
      form_name
    }
  }
`;

const CREATE_ADMISSION_FIELDS = gql`
  mutation($input: [CreateAdmissionFieldInput!]!) {
    createAdmissionField(input: $input) {
      id
    }
  }
`;

const UPDATE_ADMISSION_FIELD = gql`
  mutation($input: UpdateAdmissionFieldInput!) {
    updateAdmissionField(input: $input) {
      id
    }
  }
`;

const DELETE_ADMISSION_FIELD = gql`
  mutation($field_id: Int!) {
    deleteAdmissionField(field_id: $field_id)
  }
`;

const BASE_FIELDS = [
  { field_name: "Name of the Pupil in full(in Block Letters)", field_type_name: "text", is_required: true,field_key:"student_name" },
  { field_name: "Gender", field_type_name: "dropdown", is_required: true, options: 'Boy, Girl', field_key: "gender" },
  { field_name: "Date of Birth (in figures and words)", field_type_name: "date", is_required: true,field_key:"dob" },
  { field_name: "Name of the Father/Guardian(in Blook Letters)", field_type_name: "text", is_required: true,field_key:"father_name" },
  { field_name: "Father Phone Number", field_type_name: "number", is_required: true,field_key:"father_phone" },
  { field_name: "Name of the Mother(in Blook Letters)", field_type_name: "text", is_required: true,field_key: "mother_name" },
    { field_name: "Mother Phone Number", field_type_name: "number", is_required: true,field_key:"mother_phone" },
    { field_name: "Aadhaar Number", field_type_name: "number", is_required: false, field_key: "adhar_no" },
    { field_name: "PEN Number", field_type_name: "text", is_required: false, field_key: "pen_no" },
];

class AdmissionFormBuilder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      formDetails: {
        form_name: '',
        branch_id: props.initialBranchId ? String(props.initialBranchId) : '',
        academic_year_id: 2
      },
      existingFormId: null, // If set, we are in Edit Mode
      // Single ordered list of ALL fields. Base fields (isBase) are locked but still
      // draggable; custom fields are editable. The array order IS the form order.
      fields: [],
      deletedFieldIds: [], // Track fields to delete on save
      loadingForm: false,
      saving: false,
      error: '',
      successMsg: '',
      academicYears: [],
    };
  }

  componentDidMount() {
    this.loadAcademicYears();
    if (this.props.initialBranchId) {
      this.checkExistingFormForBranch(String(this.props.initialBranchId));
    }
  }

  // Map a base field's type name (e.g. "text", "dropdown") to a real field_type_id.
  getTypeIdByName = (name) => {
    const types = this.props.fieldTypes || [];
    const lower = String(name || '').toLowerCase();
    const found =
      types.find(t => (t.form_field_type || '').toLowerCase() === lower) ||
      types.find(t => (t.form_field_type || '').toLowerCase().includes(lower));
    return found ? found.id : (types[0] ? types[0].id : 1);
  };

  // Resolve a field_type_id back to its display name (for the base field badge).
  getTypeNameById = (id) => {
    const t = (this.props.fieldTypes || []).find(ft => ft.id === parseInt(id));
    return t ? t.form_field_type : '';
  };

  // Resolve a base field's stable field_key by its name. Custom fields have none,
  // so this returns null and they are stored without a field_key.
  getFieldKeyByName = (name) => {
    const bf = BASE_FIELDS.find(b => b.field_name.toLowerCase() === String(name || '').toLowerCase());
    return bf && bf.field_key ? bf.field_key : null;
  };

  // True only for the fixed base-field keys (student_name, dob, …). Custom fields
  // also carry a field_key now (auto-generated from their label), so a non-empty
  // field_key alone no longer implies "base" — match against the known set.
  isBaseKey = (key) => !!key && BASE_FIELDS.some(b => b.field_key === key);

  // Slugify a field label into a snake_case key: "Previous School" → "previous_school".
  slugifyKey = (name) =>
    String(name || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  // Generate a field_key for a custom field from its label, de-duplicated against
  // every key already used in the form (base + other custom) with a numeric suffix.
  generateUniqueKey = (name, usedKeys) => {
    const base = this.slugifyKey(name) || 'field';
    let key = base;
    let n = 2;
    while (usedKeys.has(key)) key = `${base}_${n++}`;
    return key;
  };

  // Build the seeded (not-yet-saved) base field objects, in their default order.
  buildBaseFields = () =>
    BASE_FIELDS.map((bf, i) => ({
      id: `base-${i}`, // temp id until saved
      isNew: true,
      isBase: true,
      field_name: bf.field_name,
      field_type_name: bf.field_type_name,
      field_type_id: this.getTypeIdByName(bf.field_type_name),
      options: bf.options || '',
      is_required: bf.is_required,
      value_length: 255,
      field_key: bf.field_key || null,
    }));

  // Resolve the stable base-field key for a loaded field. A field saved with a
  // field_key IS a base field; legacy rows saved before keys existed fall back to
  // matching their (then-unchangeable) field_name.
  baseKeyOf = (f) => (this.isBaseKey(f.field_key) ? f.field_key : null) || this.getFieldKeyByName(f.field_name);

  // Merge a form's saved fields into ONE ordered list (base + custom), sorted by their
  // saved order_no. Base fields are identified by field_key (NOT field_name, since the
  // name is now editable). Any base field missing from the DB (legacy/partial forms) is
  // seeded fresh at the front so it still shows and gets saved with its key.
  mergeLoadedFields = (loadedFields) => {
    const tagged = loadedFields
      .map(f => {
        const baseKey = this.baseKeyOf(f);
        return {
          ...f,
          // Backfill the key so legacy base rows persist it on the next save.
          field_key: baseKey || null,
          isBase: !!baseKey,
          field_type_name: this.getTypeNameById(f.field_type_id),
        };
      })
      .sort((a, b) => (a.order_no || 0) - (b.order_no || 0));

    const presentKeys = new Set(tagged.filter(f => f.isBase).map(f => f.field_key));
    const missingBase = this.buildBaseFields().filter(bf => !presentKeys.has(bf.field_key));

    return [...missingBase, ...tagged];
  };

  loadAcademicYears = async () => {
    try {
      const result = await this.props.client.query({
        query: GET_ACADEMIC_YEARS,
        variables: { id: 1 },
        fetchPolicy: 'network-only',
      });
      const academicYears = result.data.getAcademicYears || [];
      this.setState(prev => {
        // Default to current academic year if user hasn't picked one yet
        let next = prev.formDetails.academic_year_id;
        if (!prev.existingFormId && academicYears.length > 0) {
          const now = new Date();
          const startYear = now.getMonth() >= 4 ? now.getFullYear() : now.getFullYear() - 1;
          const shortStr = `${startYear}-${String(startYear + 1).slice(-2)}`;
          const longStr = `${startYear}-${startYear + 1}`;
          const defaultYear =
            academicYears.find(y => y.academic_year === shortStr) ||
            academicYears.find(y => y.academic_year === longStr) ||
            academicYears[0];
          next = String(defaultYear.id);
        }
        return {
          academicYears,
          formDetails: { ...prev.formDetails, academic_year_id: next },
        };
      });
    } catch {
      // ignore — Form.Control select will fall back to existing default
    }
  };

  // Handle checking if branch already has a form
  handleFormDetailChange = async (e) => {
    const { name, value } = e.target;
    this.setState(prev => ({
      formDetails: { ...prev.formDetails, [name]: value },
      error: '',
      successMsg: ''
    }));

    if (name === 'branch_id' && value) {
      this.checkExistingFormForBranch(value);
    } else if (name === 'branch_id' && !value) {
      this.setState({ existingFormId: null, fields: [], deletedFieldIds: [] });
    }
  };

  checkExistingFormForBranch = async (branchId) => {
    this.setState({ loadingForm: true, existingFormId: null, fields: [], deletedFieldIds: [], error: '' });
    try {
      const institute_id = parseInt(Cookies.get("institutionid"));
      const result = await this.props.client.query({
        query: GET_ADMISSION_FORMS,
        variables: { institute_id, branch_id: String(branchId) },
        fetchPolicy: 'network-only'
      });

      const forms = result.data.getAdmissionForms || [];
      if (forms.length > 0) {
        // Edit Mode!
        const existingForm = forms[0];

        // Fetch existing fields for this form
        const fieldsResult = await this.props.client.query({
            query: GET_ADMISSION_FIELDS,
            variables: { admission_form_id: existingForm.id },
            fetchPolicy: 'network-only'
        });

        const rawFields = fieldsResult.data.getAdmissionFields || [];
        const loadedFields = rawFields.map(rf => {
           let optionsStr = "";
           if (rf.options) {
             try {
                optionsStr = JSON.parse(rf.options).join(", ");
             } catch(e) {
                optionsStr = rf.options;
             }
           }

           return {
               id: rf.id, // existing ID from DB
               isNew: false,
               field_name: rf.field_name,
               field_type_id: rf.field_type_id,
               field_key: rf.field_key || null,
               options: optionsStr,
               is_required: rf.is_required,
               value_length: rf.value_length || 255,
               order_no: rf.order_no
           }
        });

        this.setState({
           existingFormId: existingForm.id,
           formDetails: {
               form_name: existingForm.form_name,
               branch_id: branchId,
               academic_year_id: existingForm.academic_year_id || 2
           },
           // One ordered list — base + custom together, by saved order.
           fields: this.mergeLoadedFields(loadedFields),
           loadingForm: false,
           successMsg: 'Form found! You are now editing the existing admission form for this branch.'
        });

      } else {
        // Fresh Create mode — seed the locked base fields so their order can be set.
        this.setState({ loadingForm: false, fields: this.buildBaseFields() });
      }
    } catch(err) {
       this.setState({ loadingForm: false, error: "Failed to check existing forms." });
    }
  };

  addField = () => {
    this.setState(prev => ({
      fields: [
        ...prev.fields,
        {
          id: Date.now(), // temporary ID
          isNew: true,
          isBase: false,
          field_name: '',
          field_type_id: '',
          options: '', // comma separated string for UI
          is_required: false,
          value_length: 255
        }
      ]
    }));
  };

  removeField = (id) => {
    this.setState(prev => {
      const fieldToRemove = prev.fields.find(f => f.id === id);
      if (fieldToRemove && fieldToRemove.isBase) return null; // base fields can't be removed
      const isExistingField = fieldToRemove && !fieldToRemove.isNew;

      return {
        fields: prev.fields.filter(f => f.id !== id),
        deletedFieldIds: isExistingField ? [...prev.deletedFieldIds, id] : prev.deletedFieldIds
      };
    });
  };

  handleFieldChange = (id, key, value) => {
    this.setState(prev => ({
      fields: prev.fields.map(f => f.id === id ? { ...f, [key]: value } : f)
    }));
  };

  // Reorder the whole field list (base + custom) by drag-and-drop. The array order is
  // the form order, and order_no is derived from it at save time.
  onFieldDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    this.setState(prev => {
      const fields = [...prev.fields];
      const [moved] = fields.splice(result.source.index, 1);
      fields.splice(result.destination.index, 0, moved);
      return { fields };
    });
  };

  validate = () => {
    const { formDetails, fields } = this.state;
    if (!formDetails.form_name.trim()) return "Form Name is required.";
    if (!formDetails.branch_id) return "Please select a branch.";

    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      if (f.isBase) {
        // Type & required are fixed for base fields, but the label is editable —
        // it just can't be blank.
        if (!String(f.field_name).trim()) return `Base field #${i + 1} label cannot be empty.`;
        continue;
      }
      if (!f.field_name.trim()) return `Custom Field #${i + 1} is missing a name.`;
      if (!f.field_type_id) return `Custom Field "${f.field_name || i + 1}" is missing a field type.`;

      const typeObj = this.props.fieldTypes.find(t => t.id === parseInt(f.field_type_id));
      if (typeObj && (typeObj.form_field_type === 'dropdown' || typeObj.form_field_type === 'radio' || typeObj.form_field_type === 'checkbox')) {
        if (!f.options.trim()) {
           return `Custom Field "${f.field_name}" is a dropdown/selection and requires comma-separated options.`;
        }
      }
    }
    return null;
  };

  handleSave = async () => {
    const error = this.validate();
    if (error) {
      this.setState({ error });
      return;
    }

    this.setState({ saving: true, error: '', successMsg: '' });

    try {
      const institute_id = parseInt(Cookies.get("institutionid"));
      const user_id = parseInt(Cookies.get("id") || Cookies.get("user_id") || "0");
      const { formDetails, fields, existingFormId, deletedFieldIds } = this.state;

      let admissionFormId = existingFormId;

      // 1. Create Form if New
      if (!existingFormId) {
          const formResult = await this.props.client.mutate({
            mutation: CREATE_ADMISSION_FORM,
            variables: {
              input: {
                form_name: formDetails.form_name.trim(),
                institute_id: institute_id,
                branch_id: String(formDetails.branch_id),
                academic_year_id: parseInt(formDetails.academic_year_id),
                inserted_by: user_id
              }
            }
          });
          admissionFormId = formResult.data.createAdmissionForm.id;
      }

      // 2. Persist every field (base + custom) in list order. order_no = array index,
      // so the unified drag-and-drop order is saved. New fields are created in a single
      // bulk call (array of objects); existing fields are updated individually.
      const createInputs = []; // { f, input } for new fields, in list order
      const updateTargets = []; // { f, input } for existing fields

      // Seed the set of keys already in use (base keys + any custom field that was
      // saved with a generated key before) so newly generated custom keys never collide.
      const usedKeys = new Set(
        fields.map(f => f.field_key || this.getFieldKeyByName(f.field_name)).filter(Boolean)
      );
      const keyByFieldId = {}; // remember the resolved key so it can persist back to state

      for (let i = 0; i < fields.length; i++) {
        const f = fields[i];

        let optionsJson = null;
        if (f.options && String(f.options).trim()) {
           const optsArray = String(f.options).split(',').map(s => s.trim()).filter(s => s);
           optionsJson = JSON.stringify(optsArray);
        }

        const fieldInput = {
          field_name: String(f.field_name).trim(),
          field_type_id: parseInt(f.field_type_id),
          options: optionsJson,
          value_length: parseInt(f.value_length) || 255,
          is_required: f.is_required,
          order_no: i + 1
        };

        // Resolve a stable field_key for every field so the backend can map values
        // back. Base fields (student_name, dob, …) keep their fixed key. Custom
        // fields keep a key once assigned, and otherwise get one generated from their
        // label (e.g. "Previous School" → "previous_school"), de-duped against the form.
        let fieldKey = f.field_key || this.getFieldKeyByName(f.field_name);
        if (!fieldKey) {
          fieldKey = this.generateUniqueKey(f.field_name, usedKeys);
          usedKeys.add(fieldKey);
        }
        keyByFieldId[f.id] = fieldKey;
        fieldInput.field_key = fieldKey;

        if (f.isNew || !existingFormId) {
          createInputs.push({ f, input: { admission_form_id: admissionFormId, ...fieldInput } });
        } else {
          updateTargets.push({ f, input: { field_id: f.id, ...fieldInput } });
        }
      }

      // 2a. Bulk-create all new fields in one mutation. Results come back in input order,
      // so we can map each created id back to its source field.
      let createdFields = [];
      if (createInputs.length) {
        const res = await this.props.client.mutate({
          mutation: CREATE_ADMISSION_FIELDS,
          variables: { input: createInputs.map(c => c.input) }
        });
        createdFields = res.data.createAdmissionField || [];
      }

      // 2b. Update existing fields individually.
      for (const u of updateTargets) {
        await this.props.client.mutate({
          mutation: UPDATE_ADMISSION_FIELD,
          variables: { input: u.input }
        });
      }

      // 2c. Reassemble the list in original order with real DB ids for created rows.
      let createdIdx = 0;
      const savedFields = fields.map(f => {
        // Persist the resolved key (including newly generated custom keys) so a
        // follow-up edit keeps the same key instead of regenerating it.
        const field_key = keyByFieldId[f.id] || f.field_key || null;
        if (f.isNew || !existingFormId) {
          const created = createdFields[createdIdx++];
          return { ...f, id: created ? created.id : f.id, field_key, isNew: false };
        }
        return { ...f, field_key, isNew: false };
      });

      // 3. Process Deletions
      for (const delId of deletedFieldIds) {
         await this.props.client.mutate({
            mutation: DELETE_ADMISSION_FIELD,
            variables: { field_id: delId }
         });
      }

      this.setState({
        saving: false,
        successMsg: `Successfully ${existingFormId ? 'updated' : 'created'} admission form "${formDetails.form_name}"!`,
        // Soft reset but keep the form in edit mode view (with real DB ids now)
        fields: savedFields,
        deletedFieldIds: [],
        existingFormId: admissionFormId
      });

      // Close the modal after a brief moment so the success message is visible.
      // The parent's close handler also refetches the admission forms list.
      if (this.props.onSaved) {
        setTimeout(() => this.props.onSaved(), 900);
      }

    } catch (err) {
      console.error(err);
      this.setState({ saving: false, error: err.message.replace('GraphQL error: ', '') });
    }
  };

  // Render a single locked base field row (draggable, but content is fixed).
  renderBaseFieldCard = (field, dragProvided, dragSnapshot) => (
    <Card
      className="mb-3 border-0 shadow-sm"
      style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: dragSnapshot.isDragging ? '0 8px 24px rgba(99,102,241,0.25)' : undefined }}
    >
      <div className="d-flex" style={{ borderLeft: `4px solid #cbd5e1`, background: '#f8fafc' }}>
        <div
          {...dragProvided.dragHandleProps}
          className="d-flex align-items-center justify-content-center px-2"
          style={{ cursor: 'grab', color: THEME.textMuted, background: THEME.lockedBg }}
          title="Drag to reorder"
        >
          <i className="fas fa-grip-vertical" />
        </div>
        <div className="flex-grow-1 p-3">
          <Row className="align-items-center">
            <Col md={6}>
              <Form.Group className="mb-md-0">
                <Form.Label style={{ fontSize: '11px', fontWeight: '700', color: THEME.textMuted, textTransform: 'uppercase' }}>
                  <i className="fas fa-lock mr-1" style={{ fontSize: '10px' }} />
                  Field Label
                  {field.field_key && <span className="ml-2 text-muted" style={{ textTransform: 'none', fontWeight: 600 }}>({field.field_key})</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={field.field_name}
                  onChange={(e) => this.handleFieldChange(field.id, 'field_name', e.target.value)}
                  style={{ borderRadius: '6px', fontWeight: '600', color: THEME.text }}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-center pt-3">
              <span className="badge badge-light border">{field.field_type_name}</span>
            </Col>
            <Col md={3} className="d-flex align-items-center justify-content-end pt-3">
              {field.is_required ? <span className="text-danger small font-weight-bold">Required</span> : <span className="text-muted small">Optional</span>}
            </Col>
          </Row>
        </div>
      </div>
    </Card>
  );

  // Render a single editable custom field row (draggable + editable).
  renderCustomFieldCard = (field, dragProvided, dragSnapshot) => {
    const { fieldTypes } = this.props;
    const isOptionsType = fieldTypes.find(t => t.id === parseInt(field.field_type_id))?.form_field_type.match(/dropdown|radio|checkbox/i);

    return (
      <Card
        className="mb-3 border-0 shadow-sm"
        style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: dragSnapshot.isDragging ? '0 8px 24px rgba(99,102,241,0.25)' : undefined }}
      >
        <div className="d-flex" style={{ borderLeft: `4px solid ${THEME.primary}` }}>
          <div
            {...dragProvided.dragHandleProps}
            className="d-flex align-items-center justify-content-center px-2"
            style={{ cursor: 'grab', color: THEME.textMuted, background: THEME.bgLight }}
            title="Drag to reorder"
          >
            <i className="fas fa-grip-vertical" />
          </div>
          <div className="flex-grow-1 p-3">
            <Row>
              <Col md={4}>
                <Form.Group className="mb-md-0">
                  <Form.Label style={{ fontSize: '11px', fontWeight: '700', color: THEME.textMuted, textTransform: 'uppercase' }}>
                    Field Name
                    {(field.field_key || this.slugifyKey(field.field_name)) && (
                      <span className="ml-2 text-muted" style={{ textTransform: 'none', fontWeight: 600 }}>
                        ({field.field_key || this.slugifyKey(field.field_name)})
                      </span>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="e.g. Previous School"
                    value={field.field_name}
                    onChange={(e) => this.handleFieldChange(field.id, 'field_name', e.target.value)}
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-md-0">
                  <Form.Label style={{ fontSize: '11px', fontWeight: '700', color: THEME.textMuted, textTransform: 'uppercase' }}>Input Type</Form.Label>
                  <Form.Control
                    as="select"
                    size="sm"
                    value={field.field_type_id}
                    onChange={(e) => this.handleFieldChange(field.id, 'field_type_id', e.target.value)}
                    style={{ borderRadius: '6px' }}
                  >
                    <option value="">-- Select --</option>
                    {fieldTypes.map(t => <option key={t.id} value={t.id}>{t.form_field_type}</option>)}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-md-0 pt-4">
                  <Form.Check
                    type="switch"
                    id={`req-switch-${field.id}`}
                    label={<span style={{ fontSize: '13px', fontWeight: '600' }}>Required</span>}
                    checked={field.is_required}
                    onChange={(e) => this.handleFieldChange(field.id, 'is_required', e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={1} className="d-flex align-items-center justify-content-end">
                <Button variant="link" className="text-danger p-0" onClick={() => this.removeField(field.id)}>
                  <i className="fas fa-trash-alt" />
                </Button>
              </Col>
            </Row>

            {isOptionsType && (
              <Row className="mt-3">
                <Col md={12}>
                  <div style={{ background: '#f1f5f9', padding: '10px 12px', borderRadius: '6px' }}>
                    <Form.Label style={{ fontSize: '11px', fontWeight: '700', color: THEME.textMuted, textTransform: 'uppercase' }}>Dropdown Options (Comma separated)</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="e.g. State Board, CBSE, ICSE"
                      value={field.options}
                      onChange={(e) => this.handleFieldChange(field.id, 'options', e.target.value)}
                      style={{ borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                  </div>
                </Col>
              </Row>
            )}
          </div>
        </div>
      </Card>
    );
  };

  render() {
    const { branches } = this.props;
    const { formDetails, fields, existingFormId, loadingForm, saving, error, successMsg } = this.state;

    return (
      <div className="py-4">
        <div className="mb-4">
          <h4 style={{ fontWeight: '800', color: THEME.text, margin: 0 }}>Admission Form Builder</h4>
          <p className="text-muted small mt-1 mb-0">Design dynamic admission forms for your branches.</p>
        </div>

        {error && <div className="alert alert-danger" style={{ borderRadius: '8px', fontWeight: '600' }}>{error}</div>}
        {successMsg && <div className="alert alert-success" style={{ borderRadius: '8px', fontWeight: '600' }}>{successMsg}</div>}

        <Row>
          {/* Master Form Settings */}
          <Col md={4} className="mb-4">
            <Card style={{ border: `1px solid ${THEME.border}`, borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <Card.Header style={{ background: '#fff', borderBottom: `1px solid ${THEME.border}`, padding: '16px 20px', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                <h6 style={{ margin: 0, fontWeight: '700', color: THEME.text }}>Form Details</h6>
              </Card.Header>
              <Card.Body style={{ padding: '20px' }}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '12px', fontWeight: '600', color: THEME.textMuted }}>Branch <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="select"
                    name="branch_id"
                    value={formDetails.branch_id}
                    onChange={this.handleFormDetailChange}
                    style={{ border: `1px solid ${THEME.border}`, borderRadius: '8px' }}
                    // Lock branch only when the user explicitly arrived via Edit
                    // (initialBranchId was provided). In Create flow, even if we
                    // auto-detect an existing form, the user can still switch branch.
                    disabled={!!this.props.initialBranchId}
                  >
                    <option value="">-- Select Branch --</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                  </Form.Control>
                  {existingFormId && <small className="text-success mt-1 d-block"><i className="fas fa-check-circle mr-1"/> Editing existing form for this branch</small>}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '12px', fontWeight: '600', color: THEME.textMuted }}>Form Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="form_name"
                    placeholder="e.g. 2026 Engineering Admission"
                    value={formDetails.form_name}
                    onChange={this.handleFormDetailChange}
                    style={{ border: `1px solid ${THEME.border}`, borderRadius: '8px' }}
                    disabled={existingFormId !== null} // Typically form name cannot be changed easily without update mutation for master form
                  />
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label style={{ fontSize: '12px', fontWeight: '600', color: THEME.textMuted }}>Academic Year</Form.Label>
                  <Form.Control
                    as="select"
                    name="academic_year_id"
                    value={formDetails.academic_year_id}
                    onChange={this.handleFormDetailChange}
                    style={{ border: `1px solid ${THEME.border}`, borderRadius: '8px' }}
                    disabled={existingFormId !== null}
                  >
                    {this.state.academicYears.length === 0 && <option value="">Loading…</option>}
                    {this.state.academicYears.map(y => (
                      <option key={y.id} value={y.id}>{y.academic_year}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* Dynamic Fields */}
          <Col md={8}>
            <Card style={{ border: `1px solid ${THEME.border}`, borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <Card.Header style={{ background: '#fff', borderBottom: `1px solid ${THEME.border}`, padding: '16px 20px', display: 'flex', alignItems: 'center', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                <h6 style={{ margin: 0, fontWeight: '700', color: THEME.text }}>Form Fields <Badge variant="light" className="ml-2 border">{fields.length}</Badge></h6>
              </Card.Header>
              <Card.Body style={{ padding: '20px', background: THEME.bgLight, minHeight: '300px' }}>

                {loadingForm ? (
                   <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                ) : !formDetails.branch_id ? (
                    <div className="text-center py-5 text-muted">
                      <i className="fal fa-building mb-3" style={{ fontSize: '32px', color: '#cbd5e1' }}></i>
                      <h6>Select a Branch</h6>
                      <p className="small mb-0">Please select a branch to view or create a form.</p>
                    </div>
                ) : (
                  <>
                    <h6 className="mb-1 text-muted" style={{ fontSize: '13px', fontWeight: '700', textTransform: 'uppercase' }}>Form Fields (Drag to Reorder)</h6>
                    <p className="text-muted small mb-3"><i className="fas fa-lock mr-1" style={{ fontSize: '11px' }}/> Locked base fields and your custom fields share one order — drag any row to position it.</p>

                    {fields.length === 0 ? (
                        <div className="text-center py-4 text-muted border rounded" style={{ background: '#fff', borderStyle: 'dashed !important' }}>
                          <i className="fal fa-list-ul mb-2" style={{ fontSize: '24px', color: '#cbd5e1' }}></i>
                          <p className="small mb-0">No fields yet.</p>
                        </div>
                    ) : (
                      <DragDropContext onDragEnd={this.onFieldDragEnd}>
                        <Droppable droppableId="form-fields-order">
                          {(dropProvided) => (
                            <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                              {fields.map((field, index) => (
                                <Draggable key={field.id} draggableId={String(field.id)} index={index}>
                                  {(dragProvided, dragSnapshot) => (
                                    <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} style={{ ...dragProvided.draggableProps.style }}>
                                      {field.isBase
                                        ? this.renderBaseFieldCard(field, dragProvided, dragSnapshot)
                                        : this.renderCustomFieldCard(field, dragProvided, dragSnapshot)}
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {dropProvided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )}

                    <div className="mt-3 d-flex justify-content-center">
                      <Button
                        variant="outline-primary"
                        onClick={this.addField}
                        style={{ borderRadius: '8px', fontWeight: '600', padding: '8px 18px' }}
                        disabled={loadingForm || !formDetails.branch_id}
                      >
                        <i className="fas fa-plus mr-2" /> Add Extra Field
                      </Button>
                    </div>
                  </>
                )}

              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Save / Update action — anchored at the bottom of the builder */}
        <div className="mt-4 d-flex justify-content-end">
          <Button
            onClick={this.handleSave}
            disabled={saving || loadingForm}
            style={{ background: THEME.success, border: 'none', fontWeight: '600', padding: '10px 24px', borderRadius: '8px' }}
          >
            {saving ? <><Spinner size="sm" animation="border" className="mr-2" /> Saving...</> : <><i className="fas fa-save mr-2"/> {existingFormId ? 'Update Form' : 'Publish Form'}</>}
          </Button>
        </div>
      </div>
    );
  }
}

export default withApollo(AdmissionFormBuilder);
